import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Button,
    Chip,
    Stack,
    Table,
    TableBody,
    TableHead,
    Typography,
} from "@mui/material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { getSubjectList } from "../../redux/sclassRelated/sclassHandle";
import { resetSubjects } from "../../redux/sclassRelated/sclassSlice";
import { getUserDetails } from "../../redux/userRelated/userHandle";
import { FORM3_MIN_SUBJECTS } from "../../utils/form3SubjectSelection";
import { dedupeSubjectsById } from "../../utils/dedupeSubjects";
import { useStudentSubjectSelectionStatus } from "../../hooks/useStudentSubjectSelectionStatus";
import { getStudentClassId } from "../../utils/userContext";
import CustomBarChart from "../../components/CustomBarChart";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import {
    StudentPageContainer,
    StudentPageHeader,
    StudentPageLoading,
    StudentViewToggle,
    StudentEmptyState,
    ResultsSectionPaper,
    ResponsiveTableWrap,
} from "./studentPortalUi";

const StudentSubjects = () => {
    const dispatch = useDispatch();
    const { subjectsList, sclassDetails } = useSelector((state) => state.sclass);
    const { userDetails, currentUser, loading } = useSelector((state) => state.user);

    const [subjectMarks, setSubjectMarks] = useState([]);
    const [view, setView] = useState("table");

    const classId = getStudentClassId(userDetails, currentUser);
    const { loading: statusLoading, status, needsSelection } = useStudentSubjectSelectionStatus();

    useEffect(() => {
        dispatch(resetSubjects());
    }, [dispatch]);

    useEffect(() => {
        if (currentUser?._id) dispatch(getUserDetails(currentUser._id, "Student"));
    }, [dispatch, currentUser?._id]);

    useEffect(() => {
        if (userDetails) setSubjectMarks(userDetails.examResult || []);
    }, [userDetails]);

    useEffect(() => {
        if (!classId || needsSelection) return;
        dispatch(getSubjectList(classId, "ClassSubjects"));
    }, [classId, needsSelection, dispatch]);

    const displaySubjects = useMemo(() => {
        if (needsSelection) return [];
        const chosenIds = new Set(
            (status?.chosenSubjectIds || []).map((id) => String(id)),
        );
        if (chosenIds.size >= FORM3_MIN_SUBJECTS && Array.isArray(status?.subjects) && status.subjects.length) {
            return dedupeSubjectsById(status.subjects);
        }
        if (chosenIds.size >= FORM3_MIN_SUBJECTS && Array.isArray(subjectsList)) {
            return dedupeSubjectsById(
                subjectsList.filter((s) => chosenIds.has(String(s._id || s.id))),
            );
        }
        return dedupeSubjectsById(subjectsList || []);
    }, [needsSelection, status, subjectsList]);

    const marksRows = useMemo(
        () =>
            (subjectMarks || []).filter((r) => r?.subName && r.marksObtained != null),
        [subjectMarks],
    );

    const hasMarks = marksRows.length > 0;
    const formLabel = status?.formLevel === "Form4" ? "Form 4" : "Form 3";

    if ((loading && !userDetails) || statusLoading) {
        return <StudentPageLoading />;
    }

    return (
        <StudentPageContainer maxWidth="lg">
            <StudentPageHeader
                eyebrow="Academics"
                title="My subjects"
                subtitle={
                    needsSelection
                        ? `Choose at least ${FORM3_MIN_SUBJECTS} subjects for your ${formLabel} subject line before using the rest of the portal.`
                        : displaySubjects.length
                          ? "Your enrolled subject line and marks when teachers publish them."
                          : "Subject marks from your class results and your class subject list."
                }
            />

            {needsSelection ? (
                <Alert
                    severity="warning"
                    sx={{ mb: 2, borderRadius: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            component={RouterLink}
                            to="/Student/form3-subjects"
                            sx={{ fontWeight: 700 }}
                        >
                            Choose subjects
                        </Button>
                    }
                >
                    You have not chosen your {formLabel} subjects yet. Pick at least {FORM3_MIN_SUBJECTS} from your
                    class list — you will not see all class subjects here until you complete your subject line.
                </Alert>
            ) : null}

            {hasMarks ? (
                <>
                    <StudentViewToggle
                        value={view}
                        onChange={setView}
                        options={[
                            { value: "table", label: "Table", icon: <TableChartOutlinedIcon fontSize="small" /> },
                            { value: "chart", label: "Chart", icon: <InsertChartOutlinedIcon fontSize="small" /> },
                        ]}
                    />
                    {view === "table" ? (
                        <ResultsSectionPaper title="Subject marks">
                            <ResponsiveTableWrap>
                                <Table size="small">
                                    <TableHead>
                                        <StyledTableRow>
                                            <StyledTableCell>Subject</StyledTableCell>
                                            <StyledTableCell align="right">Marks</StyledTableCell>
                                        </StyledTableRow>
                                    </TableHead>
                                    <TableBody>
                                        {marksRows.map((result, index) => (
                                            <StyledTableRow key={index}>
                                                <StyledTableCell>
                                                    {result.subName?.subName || result.subName}
                                                </StyledTableCell>
                                                <StyledTableCell align="right">{result.marksObtained}</StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ResponsiveTableWrap>
                        </ResultsSectionPaper>
                    ) : (
                        <ResultsSectionPaper title="Marks chart">
                            <CustomBarChart chartData={marksRows} dataKey="marksObtained" />
                        </ResultsSectionPaper>
                    )}
                </>
            ) : (
                <ResultsSectionPaper
                    title={sclassDetails?.sclassName ? `Class ${sclassDetails.sclassName}` : "Your class"}
                >
                    <StudentEmptyState
                        icon={MenuBookOutlinedIcon}
                        title={needsSelection ? "Choose your subjects first" : "No marks yet"}
                        description={
                            needsSelection
                                ? `Go to Choose subjects and select at least ${FORM3_MIN_SUBJECTS} subjects for ${formLabel}.`
                                : status?.carriedForward
                                  ? "Your teachers have not published marks yet. These are your selected subjects:"
                                  : "Your teachers have not published marks yet. These are your subjects:"
                        }
                    />
                    {!needsSelection ? (
                        <Stack spacing={1} sx={{ mt: 2 }}>
                            {displaySubjects.map((subject) => (
                                <Chip
                                    key={subject._id || subject.id}
                                    label={`${subject.subName}${subject.subCode ? ` (${subject.subCode})` : ""}`}
                                    variant="outlined"
                                />
                            ))}
                            {displaySubjects.length === 0 && (
                                <Typography color="text.secondary">No subjects listed for your class.</Typography>
                            )}
                        </Stack>
                    ) : null}
                </ResultsSectionPaper>
            )}
        </StudentPageContainer>
    );
};

export default StudentSubjects;
